<template>
  <el-dialog
    :visible.sync="visible"
    :title="ybTitle"
    width="90%"
    top="5vh"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="purchase-management" v-loading="loading">
      <!-- 顶部搜索区域 -->
      <div class="header">
        <div class="search-bar">
          <div class="search-item">
            <span class="label">医药机构：</span>
            <el-select v-model="searchParams.dimension" placeholder="请选择" size="mini">
              <el-option label="老游诊所" value="group"></el-option>
            </el-select>
          </div>
          
          <div class="search-item">
            <span class="label">医保交易：</span>
            <el-select v-model="searchParams.company" placeholder="请选择" size="mini">
              <el-option 
                v-for="company in companies" 
                :key="company.id" 
                :label="company.label" 
                :value="company.id">
              </el-option>
            </el-select>
          </div>
          
          <el-button type="primary" size="mini" @click="handleSearch">查询</el-button>
          <el-button size="mini" @click="handleReset">重置</el-button>
        </div>
      </div>
      
      <!-- 数据统计信息 -->
      <div class="data-summary" v-if="purchaseRecords.length > 0">
        <el-alert
          :title="`共查询到 ${purchaseRecords.length} 条采购记录，总金额：¥${totalAmount.toLocaleString()}`"
          type="success"
          :closable="false"
          show-icon>
        </el-alert>
      </div>
      
      <!-- 主要内容区域 -->
      <div class="main-content">
        <!-- 左侧：商品历史采购记录 -->
        <div class="left-panel">
          <div class="panel-header">
            <h3>商品历史采购记录</h3>
            <span class="record-count">(共{{ purchaseRecords.length }}条)</span>
          </div>
          <div class="panel-content">
            <div 
              v-for="(record, index) in purchaseRecords" 
              :key="index" 
              class="record-item"
              :class="{ active: selectedRecordIndex === index }"
              @click="selectRecord(record, index)"
            >
              <div class="record-info">
                <div class="record-code">{{ record.rid || `记录${index + 1}` }}</div>
                <div class="record-name">{{ record.medinsListName || '未知商品' }}</div>
                <div class="record-supplier">{{ record.splerName || '未知供应商' }}</div>
              </div>
              <div class="record-meta">
                <div class="record-date">{{ formatDate(record.purcRetnStoinTime) }}</div>
                <div class="record-quantity">数量: {{ record.purcRetnCnt || 0 }}</div>
                <div class="record-amount">金额: ¥{{ ((record.finlTrnsPric || 0) * (record.finl_trns_pric || 0)).toFixed(2) }}</div>
              </div>
            </div>
            
            <div class="empty-state" v-if="purchaseRecords.length === 0">
              <el-empty description="暂无采购记录数据" :image-size="80"></el-empty>
            </div>
          </div>
        </div>
        
        <!-- 右侧：选项卡内容 -->
        <div class="right-panel">
          <div class="panel-header">
            <el-tabs v-model="activeTab" type="card" @tab-click="handleTabChange">
              <el-tab-pane label="采购详细信息" name="detail"></el-tab-pane>
              <el-tab-pane label="数据表格分析" name="table"></el-tab-pane>
              <el-tab-pane label="数据图表分析" name="chart"></el-tab-pane>
            </el-tabs>
          </div>
          
          <div class="panel-content">
            <!-- 采购详细信息 -->
            <div v-if="activeTab === 'detail'" class="tab-content">
              <div v-if="selectedRecord" class="detail-section">
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="批次流水号">{{ selectedRecord.fixmedinsBchno || '--' }}</el-descriptions-item>
                  <el-descriptions-item label="商品名称">{{ selectedRecord.medinsListName || '--' }}</el-descriptions-item>
                  <el-descriptions-item label="商品编码">{{ selectedRecord.medinsListCodg || '--' }}</el-descriptions-item>
                  <el-descriptions-item label="供应商">{{ selectedRecord.splerName || '--' }}</el-descriptions-item>
				  <el-descriptions-item label="生产厂家">{{ selectedRecord.prodentpName || '--' }}</el-descriptions-item>
                  <el-descriptions-item label="采购时间">{{ formatDateTime(selectedRecord.purcRetnStoinTime) }}</el-descriptions-item>
                  <el-descriptions-item label="销售/退货数量">{{ selectedRecord.purcRetnCnt || 0 }}</el-descriptions-item>
                  <el-descriptions-item label="最终成交单价">¥{{ selectedRecord.finlTrnsPric || 0 }}</el-descriptions-item>
                  <el-descriptions-item label="总金额">¥{{ ((selectedRecord.purcRetnCnt * selectedRecord.finlTrnsPric)).toFixed(2) }}</el-descriptions-item>
                  <el-descriptions-item label="操作人">{{ selectedRecord.purcRetnOpterName || '--' }}</el-descriptions-item>
                  <el-descriptions-item label="生产批号">{{ selectedRecord.manuLotnum || '--' }}</el-descriptions-item>
                  <el-descriptions-item label="批准文号">{{ selectedRecord.aprvno || '--' }}</el-descriptions-item>
                </el-descriptions>
                
                <div class="action-buttons" style="margin-top: 20px;">
                  <el-button type="primary" size="mini" @click="handleUpload(selectedRecord)" :loading="uploadLoading">
                    <i class="el-icon-upload"></i> 医保（3506）商品销售退货
                  </el-button>
                  <el-button size="mini" @click="handleExport(selectedRecord)">
                    <i class="el-icon-download"></i> 导出数据
                  </el-button>
                </div>
              </div>
              
              <div class="panel-content" v-else>
                <el-empty description="请选择左侧的采购记录查看详情" :image-size="100"></el-empty>
              </div>
            </div>
            
            <!-- 数据表格分析 -->
            <div v-if="activeTab === 'table'" class="tab-content">
              <div class="analysis-header">
                <h4>采购数据统计分析</h4>
                <div class="analysis-actions">
                  <el-button type="primary" size="mini" icon="el-icon-download" @click="exportTableData">
                    导出Excel
                  </el-button>
                </div>
              </div>
              
              <el-table
                :data="tableAnalysisData"
                stripe
                border
                style="width: 100%"
                height="400"
                v-loading="tableLoading"
              >
                <el-table-column prop="splerName" label="供应商" width="150" sortable></el-table-column>
                <el-table-column prop="purchaseCount" label="采购次数" width="100" sortable></el-table-column>
                <el-table-column prop="purcRetnCnt" label="采购总量" width="100" sortable></el-table-column>
                <el-table-column prop="avgPrice" label="平均单价" width="100" sortable>
                  <template slot-scope="scope">
                    ¥{{ scope.row.avgPrice.toFixed(2) }}
                  </template>
                </el-table-column>
                <el-table-column prop="totalAmount" label="采购总额" width="120" sortable>
                  <template slot-scope="scope">
                    ¥{{ scope.row.totalAmount.toLocaleString() }}
                  </template>
                </el-table-column>
                <el-table-column prop="trend" label="趋势" width="80">
                  <template slot-scope="scope">
                    <span :class="{'trend-up': scope.row.trend === 'up', 'trend-down': scope.row.trend === 'down'}">
                      {{ scope.row.trend === 'up' ? '↑' : scope.row.trend === 'down' ? '↓' : '→' }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="progress" label="占比" width="120">
                  <template slot-scope="scope">
                    <el-progress :percentage="scope.row.progress" :show-text="false"></el-progress>
                    <span style="font-size: 12px;">{{ scope.row.progress }}%</span>
                  </template>
                </el-table-column>
              </el-table>
              
              <div class="table-summary" style="margin-top: 20px;">
                <el-row :gutter="20">
                  <el-col :span="6">
                    <el-statistic title="供应商数量" :value="supplierCount"></el-statistic>
                  </el-col>
                  <el-col :span="6">
                    <el-statistic title="总采购次数" :value="totalPurchaseCount"></el-statistic>
                  </el-col>
                  <el-col :span="6">
                    <el-statistic title="总采购金额" :value="totalAmount" prefix="¥"></el-statistic>
                  </el-col>
                  <el-col :span="6">
                    <el-statistic title="平均单价" :value="averagePrice" prefix="¥"></el-statistic>
                  </el-col>
                </el-row>
              </div>
            </div>
            
            <!-- 数据图表分析 -->
            <div v-if="activeTab === 'chart'" class="tab-content">
              <div class="chart-header">
                <h4>采购数据图表分析</h4>
                <div class="chart-type-selector">
                  <el-radio-group v-model="chartType" size="mini">
                    <el-radio-button label="bar">柱状图</el-radio-button>
                    <el-radio-button label="pie">饼图</el-radio-button>
                    <el-radio-button label="line">折线图</el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              
              <div class="chart-container">
                <!-- 柱状图 -->
                <div v-if="chartType === 'bar'" class="chart-wrapper">
                  <div class="chart-title">供应商采购金额对比</div>
                  <div class="chart-content">
                    <div class="bar-chart">
                      <div 
                        v-for="(item, index) in chartData" 
                        :key="index" 
                        class="bar-item"
                      >
                        <div class="bar-label">{{ item.supplier }}</div>
                        <div class="bar-container">
                          <div 
                            class="bar-fill" 
                            :style="{ height: (item.amount / maxChartAmount * 100) + '%' }"
                          ></div>
                        </div>
                        <div class="bar-value">¥{{ item.amount.toLocaleString() }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 饼图 -->
                <div v-if="chartType === 'pie'" class="chart-wrapper">
                  <div class="chart-title">采购金额分布</div>
                  <div class="chart-content pie-chart-content">
                    <div class="pie-chart-visual">
                      <div 
                        v-for="(slice, index) in pieChartData" 
                        :key="index" 
                        class="pie-slice"
                        :style="{
                          '--percentage': slice.percentage,
                          '--color': slice.color,
                          '--start': slice.start
                        }"
                      ></div>
                    </div>
                    <div class="pie-legend">
                      <div 
                        v-for="(item, index) in chartData" 
                        :key="index" 
                        class="legend-item"
                      >
                        <span class="legend-color" :style="{ backgroundColor: pieChartData[index].color }"></span>
                        <span class="legend-label">{{ item.supplier }}</span>
                        <span class="legend-value">¥{{ item.amount.toLocaleString() }} ({{ pieChartData[index].percentage }}%)</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 折线图 -->
                <div v-if="chartType === 'line'" class="chart-wrapper">
                  <div class="chart-title">采购趋势分析</div>
                  <div class="chart-content">
                    <div class="line-chart">
                      <div class="line-chart-grid">
                        <div class="y-axis">
                          <div 
                            v-for="(tick, index) in yAxisTicks" 
                            :key="index" 
                            class="y-tick"
                          >
                            ¥{{ tick.toLocaleString() }}
                          </div>
                        </div>
                        <div class="chart-area">
                          <div class="line-path"></div>
                          <div 
                            v-for="(item, index) in chartData" 
                            :key="index" 
                            class="data-point"
                            :style="{ left: (index * (100 / (chartData.length - 1))) + '%' }"
                          >
                            <div class="point-value">¥{{ item.amount.toLocaleString() }}</div>
                          </div>
                        </div>
                      </div>
                      <div class="x-axis">
                        <div 
                          v-for="(item, index) in chartData" 
                          :key="index" 
                          class="x-label"
                        >
                          {{ item.supplier }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="chart-summary" style="margin-top: 20px;">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <div class="summary-item">
                      <div class="summary-label">最高采购额</div>
                      <div class="summary-value">¥{{ maxChartAmount.toLocaleString() }}</div>
                      <div class="summary-supplier">{{ maxChartSupplier }}</div>
                    </div>
                  </el-col>
                  <el-col :span="8">
                    <div class="summary-item">
                      <div class="summary-label">最低采购额</div>
                      <div class="summary-value">¥{{ minChartAmount.toLocaleString() }}</div>
                      <div class="summary-supplier">{{ minChartSupplier }}</div>
                    </div>
                  </el-col>
                  <el-col :span="8">
                    <div class="summary-item">
                      <div class="summary-label">平均采购额</div>
                      <div class="summary-value">¥{{ averageChartAmount.toLocaleString() }}</div>
                    </div>
                  </el-col>
                </el-row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
// 导入医保相关API
import { ybapi } from "/src/api/ybapi-psi.js"
import { ybRequestTemplate } from "/src/untils/ybRequestTemplate.js"
import { addYbRquestData, getqdNo, toUploadLY, changeYB } from "@/api/yb";
// 导入3506商品销售退货API
import { deleteMedicalInsurance3506 } from "@/api/yb/3506DeleteYB.js";
// import { medicalInsurance3506 } from "@/api/yb/3506DeleteYB.js";

export default {
  name: 'YbPurchaseModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    queryResult: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
	  ybTitle:'医保采购信息查询结果', // 模态框标题
      loading: false,
      uploadLoading: false, // 上传加载状态
      tableLoading: false,
      activeTab: 'detail', // 默认激活第一个选项卡
      chartType: 'bar', // 默认图表类型
      searchParams: {
        dimension: 'group',
        company: 5,
        supplier: ''
      },
      companies: [
        { id:5, value: '3505', label: '商品销售(3505)' },
      ],
      purchaseRecords: [],
      selectedRecord: null,
      selectedRecordIndex: -1,
      // 表格分析数据
      tableAnalysisData: [],
      // 图表数据
      chartData: [],
      pieChartData: [],
      // 颜色配置
      chartColors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#FFC107', '#9C27B0', '#2196F3', '#4CAF50', '#FF9800']
    }
  },
  computed: {
    // 计算总金额
    totalAmount() {
      return this.purchaseRecords.reduce((sum, record) => {
        return sum + ((record.purc_retn_cnt || 0) * (record.finl_trns_pric || 0));
      }, 0);
    },
    
    // 供应商数量
    supplierCount() {
      const suppliers = new Set(this.purchaseRecords.map(record => record.spler_name));
      return suppliers.size;
    },
    
    // 总采购次数
    totalPurchaseCount() {
      return this.purchaseRecords.length;
    },
    
    // 平均单价
    averagePrice() {
      if (this.purchaseRecords.length === 0) return 0;
      const totalQuantity = this.purchaseRecords.reduce((sum, record) => sum + (record.purc_retn_cnt || 0), 0);
      return totalQuantity > 0 ? this.totalAmount / totalQuantity : 0;
    },
    
    // 图表相关计算
    maxChartAmount() {
      return Math.max(...this.chartData.map(item => item.amount), 1);
    },
    
    minChartAmount() {
      return Math.min(...this.chartData.map(item => item.amount));
    },
    
    averageChartAmount() {
      if (this.chartData.length === 0) return 0;
      return this.chartData.reduce((sum, item) => sum + item.amount, 0) / this.chartData.length;
    },
    
    maxChartSupplier() {
      if (this.chartData.length === 0) return '';
      const maxItem = this.chartData.reduce((max, item) => item.amount > max.amount ? item : max, this.chartData[0]);
      return maxItem.supplier;
    },
    
    minChartSupplier() {
      if (this.chartData.length === 0) return '';
      const minItem = this.chartData.reduce((min, item) => item.amount < min.amount ? item : min, this.chartData[0]);
      return minItem.supplier;
    },
    
    yAxisTicks() {
      const max = this.maxChartAmount;
      return [Math.floor(max * 0.75), Math.floor(max * 0.5), Math.floor(max * 0.25), 0];
    }
  },
  watch: {
    queryResult: {
      handler(newVal) {
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          this.processQueryResult(newVal);
        }
      },
      immediate: true,
      deep: true
    },
    
    activeTab(newTab) {
      // 切换选项卡时更新数据
      if (newTab === 'table') {
        this.updateTableData();
      } else if (newTab === 'chart') {
        this.updateChartData();
      }
    }
  },
  methods: {
    processQueryResult(queryResult) {
      try {
        this.loading = true;
        const resultData = Array.isArray(queryResult) ? queryResult : [];
        
        if (resultData.length > 0) {
          this.purchaseRecords = resultData;
          
          if (this.purchaseRecords.length > 0) {
            this.selectRecord(this.purchaseRecords[0], 0);
          }
          
          // 初始化表格和图表数据
          this.updateTableData();
          this.updateChartData();
        }
      } catch (error) {
        console.error('处理查询结果失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // 更新表格分析数据
    updateTableData() {
      this.tableLoading = true;
      
      try {
        // 按供应商分组统计
        const supplierMap = {};
        
        this.purchaseRecords.forEach(record => {
          const supplier = record.splerName || '未知供应商';
          
          if (!supplierMap[supplier]) {
            supplierMap[supplier] = {
              splerName: supplier,
              purchaseCount: 0,
              totalQuantity: 0,
              totalAmount: 0,
              records: []
            };
          }
          
          supplierMap[supplier].purchaseCount += 1;
          supplierMap[supplier].totalQuantity += (record.purc_retn_cnt || 0);
          supplierMap[supplier].totalAmount += ((record.purc_retn_cnt || 0) * (record.finl_trns_pric || 0));
          supplierMap[supplier].records.push(record);
        });
        
        // 转换为数组并计算附加信息
        this.tableAnalysisData = Object.values(supplierMap).map(item => {
          const avgPrice = item.totalQuantity > 0 ? item.totalAmount / item.totalQuantity : 0;
          const progress = this.totalAmount > 0 ? Math.round((item.totalAmount / this.totalAmount) * 100) : 0;
          
          return {
            ...item,
            avgPrice: avgPrice,
            trend: 'up', // 可以根据时间数据计算实际趋势
            progress: progress
          };
        });
        
        // 按总金额排序
        this.tableAnalysisData.sort((a, b) => b.totalAmount - a.totalAmount);
        
      } catch (error) {
        console.error('更新表格数据失败:', error);
      } finally {
        this.tableLoading = false;
      }
    },
    
    // 更新图表数据
    updateChartData() {
      try {
        // 使用表格分析数据作为图表数据源
        this.chartData = this.tableAnalysisData.map(item => ({
          supplier: item.supplier,
          amount: item.totalAmount
        }));
        
        // 生成饼图数据
        this.generatePieChartData();
        
      } catch (error) {
        console.error('更新图表数据失败:', error);
      }
    },
    
    // 生成饼图数据
    generatePieChartData() {
      this.pieChartData = [];
      let start = 0;
      
      this.chartData.forEach((item, index) => {
        const percentage = Math.round((item.amount / this.totalAmount) * 100);
        this.pieChartData.push({
          supplier: item.supplier,
          amount: item.amount,
          percentage: percentage,
          color: this.chartColors[index % this.chartColors.length],
          start: start
        });
        start += percentage;
      });
    },
    
    // 选项卡切换事件
    handleTabChange(tab) {
      this.activeTab = tab.name;
    },
    
    // 导出表格数据
    exportTableData() {
      this.$message.info('导出表格数据功能');
      // 这里可以实现导出逻辑
    },
    
    // 处理3506商品销售退货上传
      async handleUpload(record) {
        if (!record) {
          this.$message.warning('请选择要上传的采购记录');
          return;
        }
        
        try {
          this.uploadLoading = true;
          
          // 确认操作
          await this.$confirm('确定要执行医保商品销售退货操作吗？', '确认操作', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          });
          
          // 获取用户信息和诊所信息
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          const clinicInfo = {
            clinicCode: userInfo.clinicCode || '',
            clinicName: userInfo.clinicName || ''
          };
          
          // 创建必要的回调函数
          const addStepMessage = (message, options = {}) => {
            console.log('医保操作:', message);
            if (options.level === 'success') {
              this.$message.success(message);
            } else if (options.level === 'error') {
              this.$message.error(message);
            } else {
              this.$message.info(message);
            }
          };
          
          const addOperationStepMessage = (message) => {
            console.log('操作步骤:', message);
          };
          
          const updateOperationProgress = (progress) => {
            console.log('进度更新:', progress);
          };
          
          const updateOperationStepStatus = (step, status) => {
            console.log(`步骤 ${step} 状态:`, status);
          };
          
          // 准备采购记录数据，确保包含所有必要的字段
          const preparedRecord = this.prepareRecordFor3506(record);
          
          // 调用3506接口，传递正确的参数
          const result = await deleteMedicalInsurance3506(
            preparedRecord,
            userInfo, 
            clinicInfo,
            4, // invDataType: 4表示销售信息
            addStepMessage, 
            addOperationStepMessage, 
            updateOperationProgress,
            updateOperationStepStatus
          );
          
          if (result && result.infcode === 0) {
            this.$message.success('医保商品销售退货成功！');
          } else {
            this.$message.error(result?.err_msg || '医保商品销售退货失败');
          }
        } catch (error) {
          if (error !== 'cancel') {
            console.error('3506接口调用失败:', error);
            this.$message.error('医保商品销售退货失败: ' + (error.message || '未知错误'));
          }
        } finally {
          this.uploadLoading = false;
        }
      },
    
	// 准备记录数据用于3506接口
	  prepareRecordFor3506(record) {
	    // 确保记录包含所有必要的字段，特别是医保接口需要的字段
	    return {
	      // 从采购记录中提取数据
	      ...record,
	      
	      // 确保关键字段存在
	      medinsListCodg: record.medinsListCodg || record.fixmedinsHilistId || '',
	      fixmedinsBchno: record.fixmedinsBchno || record.fixmedinsBcHNo || '',
	      medinsListName: record.medinsListName || record.fixmedinsHilistName || '',
	      
	      // 医保接口特定字段
	      setlId: record.setlId || '',
	      psnNo: record.psnNo || '',
	      psnCertType: record.psnCertType || '',
	      prscDrCertno: record.prscDrCertno || '',
	      bilgDrName: record.bilgDrName || '',
	      pharCertType: record.pharCertType || '',
	      pharCertno: record.pharCertno || '',
	      pharName: record.pharName || '',
	      pharPracCertNo: record.pharPracCertNo || '',
	      hiFeesetlType: record.hiFeesetlType || '',
	      trdnFlag: record.trdnFlag || '',
	      mdtrtSn: record.mdtrtSn || '',
	      
	      // 采购相关字段
	      purcRetnCnt: record.purcRetnCnt || 0,
	      finlTrnsPric: record.finlTrnsPric || 0,
	      splerName: record.splerName || '',
	      prodentpName: record.prodentpName || '',
	      manuLotnum: record.manuLotnum || '',
	      aprvno: record.aprvno || '',
	      purcRetnStoinTime: record.purcRetnStoinTime || '',
	      purcRetnOpterName: record.purcRetnOpterName || ''
	    };
	  },
    
    // 获取当前诊所ID
    getCurrentClinicId() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return userInfo.clinicId || '';
    },
    
    // 获取当前机构ID
    getCurrentInstitutionId() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return userInfo.institutionId || '';
    },
    
    // 获取当前用户ID
    getCurrentUserId() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return userInfo.userId || '';
    },
    
    // 获取当前用户名
    getCurrentUserName() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return userInfo.userName || '系统用户';
    },
    
    // 其他方法保持不变
    formatDate(dateString) {
      if (!dateString) return '--';
      try {
        return dateString.split(' ')[0];
      } catch (e) {
        return dateString;
      }
    },
    
    formatDateTime(dateString) {
      if (!dateString) return '--';
      return dateString;
    },
    
    selectRecord(record, index) {
      this.selectedRecord = record;
      this.selectedRecordIndex = index;
    },
    
    handleClose() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    
    handleSearch() {
      // 搜索功能
      if (this.searchParams.company) {
        const filtered = this.queryResult.filter(item => 
          item.fixmedins_hilist_name && item.fixmedins_hilist_name.includes(this.searchParams.company)
        );
        this.processQueryResult(filtered);
      } else {
        this.processQueryResult(this.queryResult);
      }
    },
    
    handleReset() {
      this.searchParams = {
        dimension: 'group',
        company: '',
        supplier: ''
      };
      this.processQueryResult(this.queryResult);
    },
    
    handleExport(record) {
      console.log('导出记录:', record);
      this.$message.info('开始导出数据');
    }
  }
}
</script>


<style lang="less" scoped>
::v-deep .el-dialog__header{
	display: flex;
	background: linear-gradient(135deg, #1a6be0 0%, #409EFF 100%);
	padding: 12px 20px;
}
::v-deep .el-dialog__title{
	color: #fff;
}
::v-deep .el-dialog__headerbtn .el-dialog__close{
	color: #fff;
}
// 表格样式优化
::v-deep .el-table {
  .el-table__header-wrapper th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }
  
  .el-table__body tr:hover > td {
    background-color: #f0f9eb !important;
  }
}

// 图表容器优化
.chart-container {
  height: 400px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  padding: 20px;
}

// 柱状图优化
.bar-chart {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 300px;
  padding: 0 20px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
}

.bar-label {
  font-size: 12px;
  margin-bottom: 10px;
  text-align: center;
  height: 40px;
  display: flex;
  align-items: flex-end;
  font-weight: 500;
  color: #303133;
}

.bar-container {
  width: 50px;
  height: 250px;
  background: #ebeef5;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.bar-fill {
  position: absolute;
  bottom: 0;
  width: 100%;
  background: linear-gradient(to top, #409EFF, #64b5ff);
  transition: height 0.5s ease;
  border-radius: 4px 4px 0 0;
}

.bar-value {
  font-size: 12px;
  margin-top: 8px;
  font-weight: bold;
  color: #409EFF;
  background: rgba(64, 158, 255, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
}

// 饼图优化
.pie-chart-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 40px;
}

.pie-chart-visual {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  position: relative;
  background: #f0f0f0;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.pie-slice {
  position: absolute;
  width: 100%;
  height: 100%;
  clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%);
  background: conic-gradient(
    from calc(var(--start, 0) * 3.6deg),
    var(--color) 0%,
    var(--color) calc(var(--percentage) * 3.6deg),
    transparent calc(var(--percentage) * 3.6deg),
    transparent 100%
  );
}

.pie-legend {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
  }
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.legend-label {
  flex: 1;
  font-weight: 500;
  color: #303133;
}

.legend-value {
  color: #909399;
  font-size: 12px;
  min-width: 80px;
  text-align: right;
}

// 折线图优化
.line-chart {
  height: 300px;
  padding: 20px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.line-chart-grid {
  display: flex;
  height: 100%;
  position: relative;
}

.y-axis {
  width: 80px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  padding-right: 15px;
}

.y-tick {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.chart-area {
  flex: 1;
  position: relative;
  border-left: 2px solid #dcdfe6;
  border-bottom: 2px solid #dcdfe6;
}

.line-path {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, transparent 0%, rgba(64, 158, 255, 0.1) 100%);
}

.data-point {
  position: absolute;
  transform: translate(-50%, -50%);
  
  &::before {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background: #409EFF;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(64, 158, 255, 0.5);
  }
}

.point-value {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  background: #409EFF;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  white-space: nowrap;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.x-axis {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
  padding: 0 20px;
}

.x-label {
  font-size: 12px;
  color: #606266;
  text-align: center;
  font-weight: 500;
  transform: rotate(-45deg);
  white-space: nowrap;
}

// 汇总信息优化
.summary-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.summary-label {
  font-size: 14px;
  margin-bottom: 8px;
  opacity: 0.9;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
}

.summary-supplier {
  font-size: 12px;
  opacity: 0.8;
}

// 空状态优化
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
  
  .el-empty__description {
    color: #909399;
  }
}

// 响应式优化
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
    height: auto;
  }
  
  .left-panel, .right-panel {
    width: 100%;
    margin-bottom: 20px;
  }
  
  .pie-chart-content {
    flex-direction: column;
    gap: 20px;
  }
  
  .chart-container {
    height: auto;
    min-height: 400px;
  }
}
.purchase-management {
  padding: 0;
  background-color: #f5f7fa;
  min-height: 70vh;
  max-height: 70vh;
  overflow: auto;
}

.header {
  background: white;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.search-item {
  display: flex;
  align-items: center;
}

.label {
  width: 70px;
  text-align: right;
  font-size: 14px;
  color: #606266;
}

.main-content {
  display: flex;
  gap: 20px;
  height: calc(70vh - 150px);
}

.left-panel, .right-panel {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.left-panel {
  width: 28%;
}

.right-panel {
  width: 72%;
}

.panel-header {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.panel-content {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
}

.record-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.record-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  border-color: #409eff;
}

.record-item.active {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.record-info {
  flex: 1;
}

.record-code {
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.record-name {
  color: #606266;
  font-size: 14px;
}

.record-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.record-arrow {
  color: #67c23a;
  font-size: 16px;
}

.panel-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-top: 1px solid #ebeef5;
}

.pagination-info {
  color: #606266;
  font-size: 14px;
}

.page-size {
  display: flex;
  align-items: center;
}

.page-size span {
  font-size: 14px;
  color: #606266;
  margin-right: 5px;
}

.tab-content {
  height: 100%;
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section h4 {
  margin-bottom: 10px;
  color: #303133;
  font-size: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
}

.detail-label {
  width: 80px;
  text-align: right;
  color: #909399;
  font-size: 14px;
}

.detail-value {
  color: #303133;
  font-size: 14px;
}

.empty-state {
  display: flex;
  justify-content:center;
  }
// 表格分析样式
.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h4 {
    margin: 0;
    color: #303133;
  }
}

.table-summary {
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

// 图表样式
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h4 {
    margin: 0;
    color: #303133;
  }
}

.chart-container {
  height: 400px;
}

.chart-wrapper {
  height: 100%;
}

.chart-title {
  text-align: center;
  font-weight: bold;
  margin-bottom: 15px;
  color: #303133;
}

// 柱状图样式
.bar-chart {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 300px;
  padding: 0 20px;
}

.bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 60px;
}

.bar-label {
  font-size: 12px;
  margin-bottom: 10px;
  text-align: center;
  height: 40px;
  display: flex;
  align-items: flex-end;
}

.bar-container {
  width: 40px;
  height: 200px;
  background: #ebeef5;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.bar-fill {
  position: absolute;
  bottom: 0;
  width: 100%;
  background: linear-gradient(to top, #409EFF, #64b5ff);
  transition: height 0.5s ease;
}

.bar-value {
  font-size: 12px;
  margin-top: 10px;
  font-weight: bold;
  color: #409EFF;
}

// 饼图样式
.pie-chart-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.pie-chart-visual {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  position: relative;
  background: #f0f0f0;
  overflow: hidden;
}

.pie-slice {
  position: absolute;
  width: 100%;
  height: 100%;
  clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%);
  background: conic-gradient(
    from calc(var(--start, 0) * 3.6deg),
    var(--color) 0%,
    var(--color) calc(var(--percentage) * 3.6deg),
    transparent calc(var(--percentage) * 3.6deg),
    transparent 100%
  );
}

.pie-legend {
  margin-left: 30px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 10px;
}

.legend-label {
  margin-right: 10px;
  font-weight: 500;
}

.legend-value {
  color: #909399;
}

// 折线图样式
.line-chart {
  height: 300px;
  padding: 20px;
}

.line-chart-grid {
  display: flex;
  height: 100%;
}

.y-axis {
  width: 80px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  padding-right: 10px;
}

.y-tick {
  font-size: 12px;
  color: #909399;
}

.chart-area {
  flex: 1;
  position: relative;
  border-left: 1px solid #dcdfe6;
  border-bottom: 1px solid #dcdfe6;
}

.line-path {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, transparent 0%, rgba(64, 158, 255, 0.1) 100%);
}

.data-point {
  position: absolute;
  transform: translate(-50%, -50%);
}

.point-value {
  font-size: 12px;
  background: #409EFF;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.x-axis {
  display: flex;
  justify-content: space-around;
  margin-top: 10px;
}

.x-label {
  font-size: 12px;
  color: #606266;
  text-align: center;
}

// 汇总样式
.summary-item {
  text-align: center;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
}

.summary-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 5px;
}

.summary-value {
  font-size: 20px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.summary-supplier {
  font-size: 12px;
  color: #606266;
}

.trend-up {
  color: #f56c6c;
  font-weight: bold;
}

.trend-down {
  color: #67c23a;
  font-weight: bold;
}

// 响应式调整
@media (max-width: 1200px) {
  .bar-chart {
    padding: 0 10px;
  }
  
  .bar-item {
    width: 40px;
  }
  
  .bar-container {
    width: 30px;
  }
  
  .pie-chart-content {
    flex-direction: column;
  }
  
  .pie-legend {
    margin-left: 0;
    margin-top: 20px;
  }
}
</style>